import React, { createContext, useContext } from "react";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import TuneIcon from "@mui/icons-material/Tune";

// ─── Navegación del módulo (compartida por el layout con sidebar) ─────────────

export const NAV_ITEMS = [
    {
        key: "principal",
        label: "Principal",
        icon: DashboardOutlinedIcon,
        ruta: "/GestionTI/Principal",
        roles: ["Usuario", "Developer", "Admin"],
        activo: true,
    },
    {
        key: "nueva",
        label: "Nueva Solicitud",
        icon: AddCircleOutlineIcon,
        ruta: "/GestionTI/NuevaSolicitud",
        roles: ["Usuario", "Admin"],
        activo: true,
    },
    {
        key: "solicitudes",
        label: "Solicitudes",
        icon: AssignmentOutlinedIcon,
        ruta: "/GestionTI/Solicitudes",
        roles: ["Usuario", "Admin"],
        activo: false,
    },
    {
        key: "actividades",
        label: "Mis Actividades",
        icon: CheckCircleOutlineIcon,
        ruta: "/GestionTI/Actividades",
        roles: ["Developer"],
        activo: false,
    },
    {
        key: "priorizacion",
        label: "Priorización",
        icon: TuneIcon,
        ruta: "/GestionTI/Priorizacion",
        roles: ["Admin"],
        activo: false,
    },
];

// ─── Paleta semántica única para toda la solicitud ─────────────────────────────
// El color siempre significa lo mismo sin importar en qué pantalla del módulo
// se use:
//   gris  → neutro / cerrado sin más acción (Nueva, Cancelada)
//   azul  → en proceso por TI (En revisión, En definición, Asignada, En progreso, En pruebas)
//   ámbar → requiere acción de alguien (Pendiente de aprobación del usuario, Repriorizada)
//   verde → avance positivo (Aceptada, Priorizada, Completada)
//   rojo  → único estado negativo (Rechazada)

const GTI_GRIS  = { bg: "#f3f4f6", text: "#374151" };
const GTI_AZUL  = { bg: "#dbeafe", text: "#1d4ed8" };
const GTI_AMBAR = { bg: "#fef3c7", text: "#92400e" };
const GTI_VERDE = { bg: "#d1fae5", text: "#065f46" };
const GTI_ROJO  = { bg: "#fee2e2", text: "#dc2626" };

export const ESTADO_COLORES = {
    "Nueva":                               GTI_GRIS,
    "En revisión":                         GTI_AZUL,
    "Rechazada":                           GTI_ROJO,
    "Aceptada":                            GTI_VERDE,
    "En definición":                       GTI_AZUL,
    "Pendiente de aprobación del usuario": GTI_AMBAR,
    "Priorizada":                          GTI_VERDE,
    "Repriorizada":                        GTI_AMBAR,
    "Asignada":                            GTI_AZUL,
    "En progreso":                         GTI_AZUL,
    "En pruebas":                          GTI_AZUL,
    "Completada":                          GTI_VERDE,
    "Cancelada":                           GTI_GRIS,
};

export const PRIORIDAD_COLORES = {
    "Crítica": { bg: "#fecaca", text: "#991b1b" },
    "Alta":    { bg: "#fee2e2", text: "#dc2626" },
    "Media":   { bg: "#fef3c7", text: "#92400e" },
    "Baja":    { bg: "#d1fae5", text: "#15803d" },
};

export const ACTIVIDAD_ESTADO_COLORES = {
    "Pendiente":   { bg: "#f3f4f6", text: "#374151" },
    "En progreso": { bg: "#dcfce7", text: "#16a34a" },
    "Terminada":   { bg: "#d1fae5", text: "#15803d" },
};

// Agrupación de estados para la vista Admin (Principal)
export const ESTADOS_AGRUPADOS = [
    { grupo: "Ingreso",       estados: ["Nueva", "En revisión", "Rechazada"] },
    { grupo: "Análisis",      estados: ["Aceptada", "En definición", "Pendiente de aprobación del usuario"] },
    { grupo: "Planificación", estados: ["Priorizada", "Repriorizada", "Asignada"] },
    { grupo: "Ejecución",     estados: ["En progreso", "En pruebas", "Completada"] },
    { grupo: "Cerradas",      estados: ["Cancelada"] },
];

// Tipos de solicitud e impacto de negocio (deben coincidir con los CHECK
// constraints de dbo.Solicitudes en HumayaDigital_GestionProyectosTI).
// "Acceso a información" es el único tipo que lleva las 5 preguntas de
// negocio (viene del proceso de acceso a menús/reportes existentes); los
// otros 3 tipos son de desarrollo y no las usan.
export const TIPOS_SOLICITUD = ["Incidencia", "Nuevo desarrollo", "Mejora", "Acceso a información"];

export const IMPACTOS_NEGOCIO = [
    { key: "impacto_control_interno", label: "Control interno" },
    { key: "impacto_normativo",       label: "Normativo" },
    { key: "impacto_financiero",      label: "Financiero" },
];

// ─── Badge de estado/prioridad ────────────────────────────────────────────────

export const Badge = ({ texto, mapa }) => {
    const color = mapa?.[texto] ?? { bg: "#f3f4f6", text: "#6b7280" };
    return (
        <span
            className="gestionti-badge"
            style={{ background: color.bg, color: color.text }}
        >
            {texto ?? "—"}
        </span>
    );
};

// ─── Contexto de rol/usuario, provisto por GestionTILayout ────────────────────
// Evita que cada pantalla del módulo tenga que volver a pedir RolTI/Mio.

export const GestionTIContext = createContext({
    rol: null,
    empleado: null,
    cargandoRol: true,
});

export const useGestionTI = () => useContext(GestionTIContext);
